import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';

interface LocationState {
  from?: { pathname: string };
  suggestedRole?: 'student' | 'teacher';
  email?: string;
  isSocialLogin?: boolean;
  isSocialSignup?: boolean;
  provider?: string;
}

const SignupChoice: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const [selectedRole, setSelectedRole] = useState<'student' | 'teacher'>(() => {
    const suggested = state?.suggestedRole;
    return suggested === 'teacher' ? 'teacher' : 'student';
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleNext = () => {
    if (selectedRole === 'student') {
      navigate('/studentregister', { 
        state: { 
          from: state?.from || { pathname: '/' },
          ...state 
        } 
      });
    } else {
      navigate('/teacherregister', { 
        state: { 
          from: state?.from || { pathname: '/' },
          ...state 
        } 
      });
    }
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: isMobile ? '60px' : '200px',
      paddingBottom: isMobile ? '60px' : '200px',
      paddingLeft: isMobile ? '20px' : '0',
      paddingRight: isMobile ? '20px' : '0',
      background: 'var(--Color-background-normal, white)'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '410px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: isMobile ? '24px' : '40px'
      }}>
        {/* 제목 */}
        <div style={{
          alignSelf: 'stretch',
          textAlign: 'center',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          color: 'black',
          fontSize: isMobile ? '20px' : '24px',
          fontFamily: 'Pretendard, sans-serif',
          fontWeight: '500',
          lineHeight: isMobile ? '30px' : '36px',
          wordWrap: 'break-word'
        }}>
          회원가입
        </div>

        {/* 내용 */}
        <div style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: isMobile ? '16px' : '20px'
        }}>
          {/* 질문 */}
          <div style={{
            alignSelf: 'stretch',
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: 'black',
            fontSize: isMobile ? '16px' : '18px',
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: isMobile ? '24px' : '27px',
            wordWrap: 'break-word'
          }}>
            어떤 계정으로 가입을 원하세요?
          </div>

          {/* 선택 영역 */}
          <div style={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: isMobile ? '12px' : '16px'
          }}>
            {/* 역할 선택 버튼 */}
            <div style={{
              alignSelf: 'stretch',
              paddingLeft: isMobile ? '20px' : '28px',
              paddingRight: isMobile ? '20px' : '28px',
              paddingTop: isMobile ? '20px' : '24px',
              paddingBottom: isMobile ? '20px' : '24px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                alignSelf: 'stretch',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: isMobile ? '12px' : '24px'
              }}>
                {/* 학생 버튼 */}
                <div
                  onClick={() => setSelectedRole('student')}
                  style={{
                    width: isMobile ? 'auto' : '165px',
                    cursor: 'pointer',
                    flex: '1 1 0'
                  }}
                >
                  <div style={{
                    paddingLeft: isMobile ? '16px' : '20px',
                    paddingRight: isMobile ? '16px' : '20px',
                    paddingTop: isMobile ? '14px' : '16px',
                    paddingBottom: isMobile ? '14px' : '16px',
                    background: selectedRole === 'student' 
                      ? 'var(--Color-fill-normal, #293044)' 
                      : 'var(--Color-fill-white, white)',
                    borderRadius: '12px',
                    outline: selectedRole === 'student' 
                      ? 'none' 
                      : '1.20px solid var(--Color-line-normal, #F0F0F0)',
                    outlineOffset: selectedRole === 'student' ? '0' : '-1.20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: selectedRole === 'student' 
                        ? 'var(--Color-text-inverse, white)' 
                        : 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '14px' : '16px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '600',
                      lineHeight: isMobile ? '21px' : '24px',
                      wordWrap: 'break-word'
                    }}>
                      학생
                    </div>
                  </div>
                </div>

                {/* 교사 버튼 */}
                <div
                  onClick={() => setSelectedRole('teacher')}
                  style={{
                    width: isMobile ? 'auto' : '165px',
                    cursor: 'pointer',
                    flex: '1 1 0'
                  }}
                >
                  <div style={{
                    paddingLeft: isMobile ? '16px' : '20px',
                    paddingRight: isMobile ? '16px' : '20px',
                    paddingTop: isMobile ? '14px' : '16px',
                    paddingBottom: isMobile ? '14px' : '16px',
                    background: selectedRole === 'teacher' 
                      ? 'var(--Color-fill-normal, #293044)' 
                      : 'var(--Color-fill-white, white)',
                    borderRadius: '12px',
                    outline: selectedRole === 'teacher' 
                      ? 'none' 
                      : '1.20px solid var(--Color-line-normal, #F0F0F0)',
                    outlineOffset: selectedRole === 'teacher' ? '0' : '-1.20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: selectedRole === 'teacher' 
                        ? 'var(--Color-text-inverse, white)' 
                        : 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '14px' : '16px',
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '600',
                      lineHeight: isMobile ? '21px' : '24px',
                      wordWrap: 'break-word'
                    }}>
                      교사
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 다음으로 버튼 */}
            <Button
              type="button"
              width="100%"
              height="auto"
              padding="16px 20px"
              variant="primary"
              size="large"
              onClick={handleNext}
            >
              다음으로
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupChoice;
